import { isNil, startCase } from "lodash";
import cn from "classnames";
import moment from "moment";
import { Link } from "react-router-dom";
import { IColumnDefinition } from "../../../../bidding/common/table/types/ColumnDefinition";
import { ColumnBuilder } from "../../../../bidding/common/table/columns/column-builder/ColumnBuilder";
import { OriginatingTransaction } from "../../../../../types/amr-pipeline/models/OriginatingTransaction";
import { AmrTransaction } from "../../../../../types/amr-pipeline/models/AmrTransaction";
import { constants, routes, pipelineColumnLabels, pipelineTooltipText, roles } from "../../../../../constants";
import { dateTimeUtils, formatUtils, moneyUtils } from "../../../../../utils";
import StatusLabel from "../../../StatusLabel";
import { canViewTransactionDetails, getTransactionDetailsUrl, hasLimitedAccess } from "../../../../../utils/amr-pipeline.utils";
import { TransactionType } from "../../../../../types/amr-pipeline/enums/TransactionType";
import { OnHoverTooltip, Pluralize } from "../../../../common";
import { PipelineColumn } from '../../../types/PipelineColumn';
import { UserConfigColumn } from '../../../../../types/user-config/UserConfigColumn';
import { transactionStatusTitles } from "../../../../../types/amr-pipeline/enums/TransactionStatus";
import { Transaction } from "../../../../../types/amr-pipeline/models/Transaction";
import { Popover } from "../../../../controls";
import { NewDebutAmrLabel } from "../../../common/NewDebutAmrLabel";
import { MarkdownViewer, renderMarkdownOrEmpty } from "../../../../common/markdown-editor";
import { amrFormatUtils } from "../../../../../utils/amr-format.utils";
import IconSVG from "../../../../../styles/svg-icons";
import { DealUpdatesPopover } from "../../DealUpdatesPopover";
import { optionsButtonColumnDefinition, buildOptionsColumnDefinition, shareOptionColumnDefinition } from "./optionsColumn";
import { collapseStickRightColumn } from '../../../../bidding/common/table/CollapseStickRightColumn';
import { user } from "../../../../../user";
import { ImSubscriptionActionBlocker } from "../../../subscription/ImSubscriptionActionBlocker";
import { ManagerLink } from '../../../common/ManagerLink';
import { BankLink } from "../../../common/BankLink";
import { PeriodEndTooltip } from "../../../../common/PeriodEnd";
import { ImSubscriptionValue } from "../../../subscription/ImSubscriptionValue";
import { ViewNotesButton } from "../../../../notes/ViewNotesButton";
import { createColumnListBuilder } from "../../../../../utils/ColumnListBuilder";

type TColumn = IColumnDefinition<OriginatingTransaction | AmrTransaction>;

export const commonPipelineColumns = (columnsConfig: UserConfigColumn[]) => {
    const isNewColumn: TColumn = {
        columnKey: PipelineColumn.isNew,
        renderColumnHeaderContent: () => "",
        renderColumnContent: (transaction: Transaction) => <NewDebutAmrLabel transaction={transaction} />,
        className: "data-list-cell-xxs-02 data-list-cell-label",
    };

    const columnDefinitions: TColumn[] = [{
        columnKey: PipelineColumn.lastUpdated,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.lastUpdated],
        renderColumnContent: (transaction) =>
            <ImSubscriptionActionBlocker>
                {blocked =>
                    <DealUpdatesPopover
                        disabled={blocked}
                        referenceName={transaction.referenceName}
                        dealReferenceName={transaction.dealReferenceName}
                        dealLegalName={transaction.dealLegalName}
                        lastUpdated={transaction.lastUpdated}
                        type={transaction.type}
                        status={transaction.status}
                        version={transaction.version}
                        dealUpdates={(transaction as OriginatingTransaction).dealUpdates}
                    />
                }
            </ImSubscriptionActionBlocker>,
        sortingField: PipelineColumn.lastUpdated,
        className: "data-list-cell-sm-01",
    }, {
        columnKey: PipelineColumn.dealName,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.dealName],
        renderColumnContent: transaction =>
            <div className="name-with-link">
                <div className="text-ellipsis name-box">
                    <OnHoverTooltip overlay={transaction.dealLegalName}>
                        {transaction.type === TransactionType.amr || canViewTransactionDetails() ? (
                            <Link
                                target="_blank"
                                to={getTransactionDetailsUrl(transaction.type, transaction.referenceName, transaction.dealReferenceName)}
                                className="link-filter"
                            >
                                {transaction.dealLegalName}
                            </Link>
                        ) : (
                            transaction.dealLegalName
                        )}
                    </OnHoverTooltip>
                </div>
            </div>,
        sortingField: PipelineColumn.dealName,
        className: "data-list-cell-lg-02",
    }, {
        columnKey: PipelineColumn.dealTicker,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.dealTicker],
        renderColumnContent: (transaction) => transaction.dealTicker || constants.emptyPlaceholder,
        sortingField: PipelineColumn.dealTicker,
        className: "data-list-cell-lg",
    }, {
        columnKey: PipelineColumn.manager,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.manager],
        renderColumnContent: (transaction) => transaction.collateralManager
            ? <ManagerLink
                referenceName={transaction.collateralManager.referenceName}
                legalName={transaction.collateralManager.legalName}
            />
            : constants.emptyPlaceholder,
        sortingField: "collateralManager",
        className: "data-list-cell-xl",
    }, {
        columnKey: PipelineColumn.arranger,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.arranger],
        renderColumnContent: transaction => transaction.arranger
            ? <BankLink
                legalName={transaction.arranger.code ?? transaction.arranger.legalName}
                referenceName={transaction.arranger.referenceName}
                overlayText={transaction.arranger.legalName}
            />
            : constants.emptyPlaceholder,
        sortingField: PipelineColumn.arranger,
    }, {
        columnKey: PipelineColumn.trustee,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.trustee],
        renderColumnContent: transaction => transaction.trustee
            ? <OnHoverTooltip overlay={transaction.trustee?.legalName}>{transaction.trustee?.legalName}</OnHoverTooltip>
            : constants.emptyPlaceholder,
        className: "data-list-cell-lg-02",
        sortingField: PipelineColumn.trustee,
    }, {
        columnKey: PipelineColumn.transactionStatus,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.transactionStatus],
        renderColumnContent: (transaction) => <StatusLabel status={transactionStatusTitles[transaction.status]} />,
        sortingField: PipelineColumn.transactionStatus,
        className: "data-list-cell-xs-sm",
    }, {
        columnKey: PipelineColumn.transactionType,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.transactionType],
        renderColumnContent: (transaction) => startCase(transaction.type),
        sortingField: PipelineColumn.transactionType,
        className: "data-list-cell-xs-sm",
    }, {
        columnKey: PipelineColumn.dealSize,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.dealSize],
        renderColumnContent: (transaction) =>
            transaction.dealBalance
                ? moneyUtils.money(transaction.dealBalance)
                : constants.emptyPlaceholder,
        sortingField: PipelineColumn.dealSize,
        className: "data-list-cell-sm text-right",
    }, {
        columnKey: PipelineColumn.pricingDate,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.pricingDate],
        renderColumnContent: (transaction) =>
            transaction.pricingDate
                ? moment(transaction.pricingDate).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        className: "",
        sortingField: PipelineColumn.pricingDate,
    }, {
        columnKey: PipelineColumn.closingDate,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.closingDate],
        renderColumnContent: (transaction) =>
            transaction.closingDate
                ? moment(transaction.closingDate).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        className: "",
        sortingField: PipelineColumn.closingDate,
    }, {
        columnKey: PipelineColumn.nonCallPeriodEnd,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.nonCallPeriodEnd],
        renderColumnContent: ({ nonCallPeriodEnd }) =>
            nonCallPeriodEnd
                ? moment(nonCallPeriodEnd).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        sortingField: PipelineColumn.nonCallPeriodEnd,
        headerClassName: "",
        bodyClassName: "popover-column",
    }, {
        columnKey: PipelineColumn.nonCallPeriodEndDays,
        renderColumnHeaderContent: () =>
            <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.nonCallPeriodEnd]}>
                {pipelineColumnLabels[PipelineColumn.nonCallPeriodEndYears]}
                <IconSVG name="info" width={16} height={16} />
            </OnHoverTooltip>,
        renderColumnContent: (transaction) =>
            dateTimeUtils.yrsLeftToDateFrom(
                transaction.nonCallPeriodEnd,
                transaction.closingDate,
                false
            ) || constants.emptyPlaceholder,
        sortingField: PipelineColumn.nonCallPeriodEndDays,
        headerClassName: "data-list-cell-md text-right",
        bodyClassName: "data-list-cell-md text-right popover-column",
    }, {
        columnKey: PipelineColumn.outOfNC,
        renderColumnHeaderContent: () =>
            <PeriodEndTooltip
                mode="nonCall"
                title={pipelineColumnLabels[PipelineColumn.outOfNC] ?? ''}
                issuanceMonitor
            />,
        renderColumnContent: ({ outOfNC }) =>
            isNil(outOfNC)
                ? constants.emptyPlaceholder
                : formatUtils.formatBoolean(outOfNC),
        sortingField: PipelineColumn.outOfNC,
        headerClassName: "data-list-cell-sm",
        bodyClassName: "data-list-cell-sm popover-column text-capitalize",
    }, {
        columnKey: PipelineColumn.reinvestmentPeriodEnd,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.reinvestmentPeriodEnd],
        renderColumnContent: ({ reinvestmentPeriodEnd }) =>
            reinvestmentPeriodEnd
                ? moment(reinvestmentPeriodEnd).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        sortingField: PipelineColumn.reinvestmentPeriodEnd,
        headerClassName: "",
        bodyClassName: "popover-column",
    }, {
        columnKey: PipelineColumn.reinvestmentPeriodEndDays,
        renderColumnHeaderContent: () =>
            <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.reinvestmentPeriodEnd]}>
                {pipelineColumnLabels[PipelineColumn.reinvestmentPeriodEndYears]}
                <IconSVG name="info" width={16} height={16} />
            </OnHoverTooltip>,
        renderColumnContent: (transaction) =>
            dateTimeUtils.yrsLeftToDateFrom(
                transaction.reinvestmentPeriodEnd,
                transaction.closingDate,
                false
            ) || constants.emptyPlaceholder,
        sortingField: PipelineColumn.reinvestmentPeriodEndDays,
        headerClassName: "data-list-cell-md text-right",
        bodyClassName: "data-list-cell-md text-right popover-column",
    }, {
        columnKey: PipelineColumn.outOfRI,
        renderColumnHeaderContent: () =>
            <PeriodEndTooltip
                mode="reinvestment"
                title={pipelineColumnLabels[PipelineColumn.outOfRI] ?? ''}
                issuanceMonitor
            />,
        renderColumnContent: ({ outOfRI }) =>
            isNil(outOfRI)
                ? constants.emptyPlaceholder
                : formatUtils.formatBoolean(outOfRI),
        sortingField: PipelineColumn.outOfRI,
        headerClassName: "data-list-cell-sm-01",
        bodyClassName: "data-list-cell-sm-01 popover-column text-capitalize",
    }, {
        columnKey: PipelineColumn.was,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.was],
        renderColumnContent: (transaction) => (
            <ImSubscriptionValue>
                {
                    formatUtils.formatDecimal((transaction as OriginatingTransaction).was)
                    || constants.emptyPlaceholder
                }
            </ImSubscriptionValue>
        ),
        className: "data-list-cell-xs-sm text-right",
        sortingField: PipelineColumn.was,
    }, {
        columnKey: PipelineColumn.warf,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.warf],
        renderColumnContent: (transaction) => {
            const warf = (transaction as OriginatingTransaction).warf;
            return (
                <ImSubscriptionValue>
                    {warf ? moneyUtils.money(warf, true) : constants.emptyPlaceholder}
                </ImSubscriptionValue>
            );
        },
        className: "data-list-cell-xs-sm text-right",
        sortingField: PipelineColumn.warf,
    }, {
        columnKey: PipelineColumn.ds,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.ds],
        renderColumnContent: transaction => (
            <ImSubscriptionValue>
                {
                    formatUtils.formatDecimal((transaction as OriginatingTransaction).ds)
                    || constants.emptyPlaceholder
                }
            </ImSubscriptionValue>
        ),
        className: "data-list-cell-xxs-02 text-right",
        sortingField: PipelineColumn.ds,
    }, {
        columnKey: PipelineColumn.euCompliance,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.euCompliance],
        renderColumnContent: transaction => {
            const columnText = formatUtils.formatBooleanWithPlaceholder(transaction.euCompliance);

            if (
                transaction.euCompliance &&
                (transaction.euRiskRetentionRequirements || transaction.euDisclosureRequirements)
            ) {
                return (
                    <Popover
                        title="EU Compliance"
                        className="popover-eu-compliance"
                        label={
                            <span className="name-tag text-ellipsis">
                                {transaction.dealLegalName}
                            </span>
                        }
                        actionButton={
                            <button className="pseudo-link">
                                {columnText}
                            </button>
                        }
                    >
                        <h2 className="title-border">
                            EU Risk Retention Requirements
                        </h2>
                        <div className="popover-text-box">
                            {renderMarkdownOrEmpty(transaction.euRiskRetentionRequirements)}
                        </div>
                        <h2 className="title-border">
                            EU Disclosure Requirements
                        </h2>
                        <div>
                            {renderMarkdownOrEmpty(transaction.euDisclosureRequirements)}
                        </div>
                    </Popover>
                );
            }

            return columnText;
        },
        className: "",
        sortingField: PipelineColumn.euCompliance,
    }, {
        columnKey: PipelineColumn.esg,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.esg],
        renderColumnContent: (transaction) => {
            const { dealLegalName, esg, esgComment } = transaction;
            const columnText = formatUtils.formatBooleanWithPlaceholder(esg);

            if (esg && esgComment) {
                return (
                    <Popover
                        title="ESG Comment"
                        label={
                            <span className="name-tag text-ellipsis">
                                {dealLegalName}
                            </span>
                        }
                        actionButton={
                            <button className="pseudo-link">
                                {columnText}
                            </button>
                        }
                        className="popover-esg-comments"
                    >
                        <MarkdownViewer value={esgComment} />
                    </Popover>
                );
            }

            return columnText;
        },
        className: "data-list-cell-xs",
        sortingField: PipelineColumn.esg,
    }, {
        columnKey: PipelineColumn.collateralType,
        renderColumnHeaderContent: () => "Collat.",
        renderColumnContent: (transaction) =>
            formatUtils.formatCollateralType((transaction as OriginatingTransaction).collateralType),
        className: "data-list-cell-xs",
        sortingField: PipelineColumn.collateralType,
    }, {
        columnKey: PipelineColumn.currency,
        renderColumnHeaderContent: () => "Ccy",
        renderColumnContent: (transaction) =>
            (transaction as OriginatingTransaction).currency?.code ||
            constants.emptyPlaceholder,
        className: "data-list-cell-xxs-02",
        sortingField: PipelineColumn.currency,
    }, {
        columnKey: PipelineColumn.amrDeal,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.amrDeal],
        renderColumnContent: (transaction) =>
            formatUtils.formatBooleanWithPlaceholder((transaction as OriginatingTransaction).amr),
        className: "data-list-cell-sm",
        sortingField: PipelineColumn.amr,
    }, {
        columnKey: PipelineColumn.transactionSize,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.transactionSize],
        renderColumnContent: (transaction) =>
            moneyUtils.money(transaction.dealBalance) ||
            constants.emptyPlaceholder,
        className: "data-list-cell-sm-02 text-right",
        sortingField: "dealBalance",
    }, {
        columnKey: PipelineColumn.offeringType,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.offeringType],
        renderColumnContent: (transaction) => (
            <>
                {!!(transaction as OriginatingTransaction).offeringType ? (
                    <OnHoverTooltip
                        overlay={(transaction as OriginatingTransaction).offeringType}
                    >
                        {(transaction as OriginatingTransaction).offeringType}
                    </OnHoverTooltip>
                ) : (constants.emptyPlaceholder)}
            </>
        ),
        className: "data-list-cell-sm-01",
        sortingField: PipelineColumn.offeringType,
    }, {
        columnKey: PipelineColumn.maturity,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.maturity],
        renderColumnContent: (transaction) => {
            const maturity =
                (transaction as OriginatingTransaction).statedMaturity ||
                (transaction as AmrTransaction).finalMaturity;
            return maturity
                ? moment(maturity).format(constants.dateFormat)
                : constants.emptyPlaceholder;
        },
        className: "",
        sortingField: PipelineColumn.maturity,
    }, {
        columnKey: PipelineColumn.vintage,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.vintage],
        renderColumnContent: (transaction) => {
            const vintage = (transaction as OriginatingTransaction).vintage
            return vintage ? moment(vintage).year() : constants.emptyPlaceholder;
        },
        sortingField: PipelineColumn.vintage,
        className: "data-list-cell-xs-sm"
    }, {
        columnKey: PipelineColumn.firstPayment,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.firstPayment],
        renderColumnContent: (transaction) =>
            transaction.firstPaymentDate
                ? moment(transaction.firstPaymentDate).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        className: "data-list-cell-sm-01",
        sortingField: PipelineColumn.firstPayment,
    }, {
        columnKey: PipelineColumn.intex,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.intex],
        renderColumnContent: (transaction) => {
            const { intexName, intexPassword } = transaction as OriginatingTransaction;
            return !intexName && !intexPassword ? (
                constants.emptyPlaceholder
            ) : (
                <OnHoverTooltip
                    overlay={
                        (intexName || constants.emptyPlaceholder) +
                        " / " +
                        (intexPassword || constants.emptyPlaceholder)
                    }
                >
                    {(intexName || constants.emptyPlaceholder) +
                        " / " +
                        (intexPassword || constants.emptyPlaceholder)}
                </OnHoverTooltip>
            );
        },
        className: "data-list-cell-xl-xxl",
    }, {
        columnKey: PipelineColumn.numberOfDocuments,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.numberOfDocuments],
        renderColumnContent: (transaction, { userCompany }) => {
            const isMedia = user.hasRoles(roles.Media);

            if (!transaction.numberOfDocuments) {
                return constants.emptyPlaceholder;
            }

            return !isMedia && (transaction.type === TransactionType.amr || (canViewTransactionDetails() && !hasLimitedAccess(transaction, userCompany))) ? (
                <a
                    className="text-ellipsis"
                    href={routes.transactionDetailUrl(
                        transaction.referenceName,
                        transaction.dealReferenceName,
                        "documents"
                    )}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    View ({transaction.numberOfDocuments})
                </a>
            ) : (
                <span className="text-ellipsis">
                    {transaction.numberOfDocuments}
                </span>
            );
        },
        sortingField: PipelineColumn.numberOfDocuments,
        className: "data-list-cell-xs"
    }, {
        columnKey: PipelineColumn.syndicate,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.syndicate],
        renderColumnContent: (transaction, context) => {
            const { dispatch, actions } = context;
            const numberOfContacts = (transaction as OriginatingTransaction).numberOfSyndicateContacts;
            return numberOfContacts ? (
                <span
                    className="text-ellipsis pseudo-link"
                    onClick={(e) => {
                        e.stopPropagation();
                        dispatch(
                            actions.setSyndicateContacts(
                                transaction.dealLegalName,
                                transaction.dealReferenceName,
                                transaction.referenceName,
                                transaction.arranger?.legalName
                            )
                        );
                    }}
                >
                    <Pluralize singular="contact" count={numberOfContacts} />
                </span>
            ) : (constants.emptyPlaceholder);
        },
        className: "",
        sortingField: PipelineColumn.syndicate,
    }, {
        columnKey: PipelineColumn.seniorManagementFee,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.seniorManagementFee],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return formatUtils.formatDecimal(originatingTransacton.seniorManagementFee) || constants.emptyPlaceholder
        },
        className: "data-list-cell-sm text-right",
        sortingField: PipelineColumn.seniorManagementFee,
    }, {
        columnKey: PipelineColumn.subManagementFee,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.subManagementFee],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return formatUtils.formatDecimal(originatingTransacton.subManagementFee) || constants.emptyPlaceholder
        },
        className: "data-list-cell-sm text-right",
        sortingField: PipelineColumn.subManagementFee,
    }, {
        columnKey: PipelineColumn.incentiveManagementFee,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.incentiveManagementFee],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return formatUtils.formatDecimal(originatingTransacton.incentiveManagementFee) || constants.emptyPlaceholder
        },
        className: "text-right",
        sortingField: PipelineColumn.incentiveManagementFee,
    }, {
        columnKey: PipelineColumn.adminExpenseCapPercentage,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.adminExpenseCapPercentage],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return formatUtils.formatDecimal(originatingTransacton.adminExpenseCapPercentage) || constants.emptyPlaceholder
        },
        className: "data-list-cell-sm text-right",
        sortingField: PipelineColumn.adminExpenseCapPercentage,
    }, {
        columnKey: PipelineColumn.adminExpenseCap,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.adminExpenseCap],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return originatingTransacton.adminExpenseCap
                ? moneyUtils.money(originatingTransacton.adminExpenseCap, true, 0)
                : constants.emptyPlaceholder
        },
        className: "data-list-cell-sm-02 text-right",
        sortingField: PipelineColumn.adminExpenseCap,
    }, {
        columnKey: PipelineColumn.trusteeFee,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.trusteeFee],
        renderColumnContent: transaction => {
            const originatingTransacton = transaction as OriginatingTransaction;
            return formatUtils.formatDecimal(originatingTransacton.trusteeFee) || constants.emptyPlaceholder
        },
        className: "data-list-cell-sm-01 text-right",
        sortingField: PipelineColumn.trusteeFee,
    }, {
        columnKey: PipelineColumn.notes,
        renderColumnHeaderContent: () =>
            <OnHoverTooltip overlay="Private or company-shared notes.">
                {pipelineColumnLabels[PipelineColumn.notes]}
                <IconSVG name="info" width={16} height={16} />
            </OnHoverTooltip>,
        renderColumnContent: (transaction, context) =>
            <ViewNotesButton
                notesCount={transaction.notesCount ?? 0}
                onClick={() => context.setNotesTransaction?.(transaction)}
            />,
    }, {
        columnKey: PipelineColumn.enhancedCLO,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.enhancedCLO],
        renderColumnContent: (transaction) =>
            formatUtils.formatBooleanWithPlaceholder(transaction.enhancedCLO),
        className: "data-list-cell-sm",
        sortingField: PipelineColumn.enhancedCLO,
    }, {
        columnKey: PipelineColumn.staticDeal,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.staticDeal],
        renderColumnContent: (transaction) => formatUtils.formatBooleanWithPlaceholder(transaction.staticDeal),
        className: "data-list-cell-sm-02",
        sortingField: PipelineColumn.staticDeal,
    }, {
        columnKey: PipelineColumn.rollerDeadline,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.rollerDeadline],
        renderColumnContent: (transaction) => {
            const { rollerDeadline } = transaction as OriginatingTransaction;
            return rollerDeadline
                ? <div className={cn({ today: dateTimeUtils.isToday(rollerDeadline) })}>
                    {amrFormatUtils.formatRollerDeadlineDate(moment(rollerDeadline))}
                </div>
                : constants.emptyPlaceholder;
        },
        className: "data-list-cell-lg-01",
        sortingField: PipelineColumn.rollerDeadline,
    }, {
        columnKey: PipelineColumn.iois,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.iois],
        renderColumnContent: (transaction) =>
            formatUtils.formatBooleanWithPlaceholder(
                transaction.type !== TransactionType.amr &&
                (transaction as OriginatingTransaction).classes.some(c => c.numberOfIois)
            ),
        className: "data-list-cell-xxs",
    }];

    const isIoIButtonAvailable = user.hasRoles(
        ...roles.getAllRolesExclude(
            ...roles.admin(),
            roles.DataEntry,
            roles.Viewer,
            roles.Media,
        )
    );
    const isShareAvailable = user.hasRoles(...roles.bd());
    const isBdOrSeller = user.hasRoles(roles.SellerTrader, roles.BrokerDealerTrader);

    const asOptionColumn = (column: TColumn): TColumn => {
        return {
            ...column,
            configKey: PipelineColumn.options
        } as unknown as TColumn;
    };

    const configuredColumns = createColumnListBuilder<OriginatingTransaction | AmrTransaction>(columnsConfig, true)
        .add(...columnDefinitions)
        .add({ ...collapseStickRightColumn, columnKey: PipelineColumn.options })
        .addWhen(() => isIoIButtonAvailable, asOptionColumn(optionsButtonColumnDefinition))
        .addWhen(() => isShareAvailable, asOptionColumn(shareOptionColumnDefinition))
        .add(asOptionColumn(buildOptionsColumnDefinition(isBdOrSeller)))
        .result()
        .map(x => new ColumnBuilder(x));

    return [new ColumnBuilder(isNewColumn)].concat(configuredColumns);
};
